import type { AppData, Product, Service, PrintMaterial, Sale, Customer, Vendor, StockMovement, Purchase, PaymentMethod, SaleItem } from "./types";

const todayAt = (hour: number, minute = 0) => {
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
};

const createId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const products: Product[] = [
  {
    id: "p-1",
    name: "Glossy Photo Print 4×6",
    category: "Photo & Print",
    productCode: "PRD-1001",
    barcode: "8901001001",
    sellingPrice: 25,
    partyPrice: 20,
    costPrice: 12,
    stockQuantity: 86,
    lowStockThreshold: 20,
  },
  {
    id: "p-2",
    name: "A4 Premium Paper",
    category: "Stationery",
    productCode: "PRD-1002",
    barcode: "8901001002",
    sku: "PAPER-A4",
    sellingPrice: 12,
    costPrice: 8,
    stockQuantity: 14,
    lowStockThreshold: 20,
  },
  {
    id: "p-3",
    name: "Blue Ball Pen",
    category: "Stationery",
    productCode: "PRD-1003",
    barcode: "8901001003",
    sellingPrice: 20,
    partyPrice: 15,
    costPrice: 10,
    stockQuantity: 42,
    lowStockThreshold: 15,
  },
  {
    id: "p-4",
    name: "Document Folder",
    category: "Stationery",
    productCode: "PRD-1004",
    barcode: "8901001004",
    sellingPrice: 85,
    costPrice: 50,
    stockQuantity: 9,
    lowStockThreshold: 10,
  },
  {
    id: "p-5",
    name: "Passport Photo Sheet",
    category: "Photo & Print",
    productCode: "PRD-1005",
    barcode: "8901001005",
    sellingPrice: 120,
    partyPrice: 100,
    costPrice: 55,
    stockQuantity: 31,
    lowStockThreshold: 8,
  },
  {
    id: "p-6",
    name: "Gift Wrap Paper",
    category: "Gift & Wrap",
    productCode: "PRD-1006",
    barcode: "8901001006",
    sellingPrice: 60,
    partyPrice: 45,
    costPrice: 30,
    stockQuantity: 5,
    lowStockThreshold: 8,
  },
];

const printMaterials: PrintMaterial[] = [
  { id: "pm-1", name: "Star Flex Paper", unit: "sq ft", costPerUnit: 28 },
  { id: "pm-2", name: "Premium Flex Paper", unit: "sq ft", costPerUnit: 40 },
  { id: "pm-3", name: "T-Shirt Transfer Vinyl", unit: "pcs", costPerUnit: 90 },
];

const services: Service[] = [
  { id: "s-1", name: "Photo Print — Passport", sellingPrice: 250 },
  { id: "s-2", name: "Document Lamination", sellingPrice: 80 },
  { id: "s-3", name: "Photo Frame Repair", sellingPrice: 450 },
  { id: "s-4", name: "Colour Document Print", sellingPrice: 35 },
  {
    id: "s-5",
    name: "Flex Print",
    sellingPrice: 0,
    kind: "printing",
    rates: [
      { materialId: "pm-1", pricePerUnit: 45 },
      { materialId: "pm-2", pricePerUnit: 60 },
    ],
  },
  {
    id: "s-6",
    name: "T-Shirt Print",
    sellingPrice: 0,
    kind: "printing",
    rates: [{ materialId: "pm-3", pricePerUnit: 250 }],
  },
];

const customers: Customer[] = [
  {
    id: "c-1",
    name: "Maya Sharma",
    phone: "9800000001",
    customerType: "retail",
    email: "maya@example.com",
    address: "Thamel, Kathmandu",
    notes: "Prefers digital wallet",
    createdAt: todayAt(9),
  },
  {
    id: "c-2",
    name: "Ramesh Gurung",
    phone: "9800000002",
    customerType: "retail",
    address: "Baneshwor, Kathmandu",
    createdAt: todayAt(9),
  },
  {
    id: "c-3",
    name: "Sunil Traders",
    phone: "9800000003",
    customerType: "party",
    address: "Balkhu, Kathmandu",
    notes: "Buys in bulk · party price",
    createdAt: todayAt(9),
  },
];

const vendors: Vendor[] = [
  {
    id: "v-1",
    name: "Kathmandu Paper Suppliers",
    phone: "9810000001",
    address: "Kalimati, Kathmandu",
    notes: "Weekly delivery on Mondays",
    createdAt: todayAt(8),
  },
  {
    id: "v-2",
    name: "Nepal Packaging House",
    phone: "9810000002",
    address: "Patan, Lalitpur",
    createdAt: todayAt(8),
  },
];

const paymentMethods: PaymentMethod[] = [
  { id: "cash", name: "Cash", isDefault: true },
  { id: "wallet", name: "Digital Wallet" },
];

const seedSale = (
  id: string,
  invoiceNumber: string,
  hour: number,
  items: SaleItem[],
  paymentMethod: string,
  totalAmount: number,
  customerId?: string,
  paidAmount?: number
): Sale => ({
  id,
  invoiceNumber,
  status: "completed",
  totalAmount,
  discountAmount: 0,
  paymentMethod,
  customerId,
  paidAmount: paidAmount ?? totalAmount,
  createdAt: todayAt(hour),
  items,
});

const sales: Sale[] = [
  seedSale(
    "sale-1",
    "#1048",
    9,
    [
      {
        id: "si-1",
        itemType: "product",
        productId: "p-1",
        itemName: "Glossy Photo Print 4×6",
        quantity: 4,
        unitPrice: 25,
        lineTotal: 100,
      },
    ],
    "Cash",
    100
  ),
  seedSale(
    "sale-2",
    "#1047",
    10,
    [
      {
        id: "si-2",
        itemType: "product",
        productId: "p-2",
        itemName: "A4 Premium Paper",
        quantity: 3,
        unitPrice: 12,
        lineTotal: 36,
      },
      {
        id: "si-3",
        itemType: "service",
        serviceId: "s-4",
        itemName: "Colour Document Print",
        quantity: 2,
        unitPrice: 35,
        lineTotal: 70,
      },
    ],
    "Digital Wallet",
    106
  ),
  seedSale(
    "sale-3",
    "#1046",
    11,
    [
      {
        id: "si-4",
        itemType: "product",
        productId: "p-5",
        itemName: "Passport Photo Sheet",
        quantity: 1,
        unitPrice: 120,
        lineTotal: 120,
      },
    ],
    "Cash",
    120
  ),
  seedSale(
    "sale-4",
    "#1045",
    13,
    [
      {
        id: "si-5",
        itemType: "service",
        serviceId: "s-3",
        itemName: "Photo Frame Repair",
        quantity: 1,
        unitPrice: 450,
        lineTotal: 450,
        advanceAmount: 200,
        balanceAmount: 250,
      },
    ],
    "Cash",
    450,
    "c-1",
    200
  ),
  seedSale(
    "sale-6",
    "#1043",
    14,
    [
      {
        id: "si-7",
        itemType: "product",
        productId: "p-1",
        itemName: "Glossy Photo Print 4×6",
        quantity: 10,
        unitPrice: 20,
        lineTotal: 200,
      },
    ],
    "Cash",
    200,
    "c-3",
    150
  ),
  seedSale(
    "sale-5",
    "#1044",
    15,
    [
      {
        id: "si-6",
        itemType: "product",
        productId: "p-3",
        itemName: "Blue Ball Pen",
        quantity: 5,
        unitPrice: 20,
        lineTotal: 100,
      },
    ],
    "Cash",
    100
  ),
];

const credits = [
  {
    id: "credit-1",
    saleId: "sale-4",
    customerId: "c-1",
    customerName: "Maya Sharma",
    customerPhone: "9800000001",
    itemType: "service" as const,
    itemName: "Photo Frame Repair",
    advancePaid: 200,
    amountDue: 250,
    status: "outstanding" as const,
    soldAt: todayAt(13),
  },
  {
    id: "credit-2",
    saleId: "sale-6",
    customerId: "c-3",
    customerName: "Sunil Traders",
    customerPhone: "9800000003",
    itemType: "product" as const,
    itemName: "Glossy Photo Print 4×6",
    advancePaid: 150,
    amountDue: 50,
    status: "outstanding" as const,
    soldAt: todayAt(14),
  },
];

const stockMovements: StockMovement[] = [
  ...products.map(product => ({
    id: createId("movement"),
    productId: product.id,
    type: "opening" as const,
    quantityChange: product.stockQuantity,
    resultingQuantity: product.stockQuantity,
    reason: "Opening stock",
    createdAt: todayAt(8),
  })),
  {
    id: createId("movement"),
    productId: "p-2",
    type: "purchase" as const,
    quantityChange: 4,
    resultingQuantity: 14,
    reason: "Purchase from Kathmandu Paper Suppliers",
    createdAt: todayAt(7),
  },
  {
    id: createId("movement"),
    productId: "p-6",
    type: "purchase" as const,
    quantityChange: 10,
    resultingQuantity: 5,
    reason: "Purchase from Kathmandu Paper Suppliers",
    createdAt: todayAt(7),
  },
];

const purchases: Purchase[] = [
  {
    id: "purchase-1",
    vendorId: "v-1",
    vendorName: "Kathmandu Paper Suppliers",
    items: [
      {
        id: "pi-1",
        productId: "p-2",
        itemName: "A4 Premium Paper",
        quantity: 4,
        unitPrice: 8,
        lineTotal: 32,
      },
      {
        id: "pi-2",
        productId: "p-6",
        itemName: "Gift Wrap Paper",
        quantity: 10,
        unitPrice: 30,
        lineTotal: 300,
      },
    ],
    totalAmount: 332,
    paidAmount: 200,
    dueAmount: 132,
    paymentStatus: "partial",
    status: "completed",
    createdAt: todayAt(7),
  },
];

export const SEED_DATA: AppData = {
  products,
  services,
  printMaterials,
  paymentMethods,
  sales,
  credits,
  dueCollections: [],
  stockMovements,
  customers,
  vendors,
  purchases,
  expenses: [],
  dateFormat: "BS",
};

/** Live-data boot state: the app hydrates this from the API, never from mock. */
export const EMPTY_DATA: AppData = {
  products: [],
  services: [],
  printMaterials: [],
  paymentMethods: [],
  sales: [],
  credits: [],
  dueCollections: [],
  stockMovements: [],
  customers: [],
  vendors: [],
  purchases: [],
  expenses: [],
  dateFormat: "BS",
};
